import React, { useMemo, useState } from "react";
import { Box, Button, Popover, Typography } from "@mui/material";
import { legendEntryFor } from "./eventColors";
import useIsMobile from "../../../Core/hook/useIsMobile";

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
);

/**
 * Small mode/location legend. Only shows entries present in the currently
 * visible events (`rows`), so the legend never advertises a color that isn't
 * on screen. Collapses to a single "Legend" popover on mobile.
 */
const LegendBar: React.FC<{ rows: any[] }> = ({ rows }) => {
  const isMobile = useIsMobile();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const entries = useMemo(() => {
    const map = new Map<string, { label: string; color: string }>();
    (rows || []).forEach((r) => {
      const e = legendEntryFor(r);
      if (!map.has(e.key)) map.set(e.key, { label: e.label, color: e.color });
    });
    return Array.from(map.values());
  }, [rows]);

  if (entries.length === 0) return null;

  const content = (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
      {entries.map((e) => (
        <Box key={e.label} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Dot color={e.color} />
          <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontWeight: 600 }}>{e.label}</Typography>
        </Box>
      ))}
    </Box>
  );

  if (isMobile) {
    return (
      <>
        <Button
          size="small"
          variant="outlined"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, fontSize: 12 }}
        >
          Legend
        </Button>
        <Popover
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box sx={{ p: 1.25, maxWidth: 260 }}>{content}</Box>
        </Popover>
      </>
    );
  }

  return content;
};

export default LegendBar;
